import { LoaderFunction } from 'react-router-dom';
import { DocumentNode } from '@apollo/client';
import * as Sentry from '@sentry/react';

// Types
import { AuthContextType } from '../../../../hooks/useAuth';
import { SurveillanceRoute } from '../../../../types/surveillanceRoute';
import {
  OperatorsSelectionType,
  VehiclesSelectionType,
} from '../../../../components/display/tables/formTables/OperatorsSelectionTable/types';

// GQL
import { client } from '../../../../gql/client';
import { GET_SURVEILLANCE_ROUTE_FOR_EDIT } from '../../../../gql/queries/surveillanceRoute';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { GET_ROADS, GET_ROAD_BY_ID } from '../../../../gql/queries/geoPositions/roads';
import { readSurveillanceRoutePresets } from '../../../../gql/selectors/surveillanceRoutePresets';
import { preferServerWithCacheFallback } from '../../../../gql/cacheFallback';
import { FETCH_SURVEILLANCE_TEAMS } from '../../../../gql/queries/users';
import { GET_VEHICLE_OCCUPANT_BY_ID } from '../../../../gql/queries/vehicles';
import { GET_ORDERS_BY_IDS_SERVER } from '../../../../gql/queries/orders/orders';
import { selectActiveOperatorsAndVehicles } from '../../../../gql/selectors/actions';
import { buildRouteCrewSeed } from './crewSeed';

// Static data
import { hoursType } from '../../../../data/orders';

// Utils
import { pkFormatter } from '../../../../utils/transform';
import { VehicleUsage } from '../../../../types/vehicles';

// ═══════════════════════════════════════════════════════════════════
// LOADER — wizard EditRoute (F4 2026-07-17)
//
// Espejo de EditCampaign/loader: precarga la ENTIDAD SurveillanceRoute + las
// opciones de selects del wizard (mismas que NewRoute/loader), reconstruye la
// tripulación actual (operarios + parejas vehículo→conductor) para sembrar la
// tab Operaris, y adjunta un `dataToCompare` (snapshot para el diff del
// guardado, patrón `EditAccident/loader`). La forma de datos de nivel superior
// es compatible con `NewRouteLoaderData` (concessions/roads/internalOperators/
// externalCompanies/hoursTypeOptions/teams) porque el wizard NewRoute lee su
// `useAsyncValue` de este MISMO objeto; los campos `edit*`/`dataToCompare`/
// `hasSegments`/`routeId` son extras que consume la página EditRoute.
// ═══════════════════════════════════════════════════════════════════

/** Snapshot canónico del RV para el diff del guardado (números/ids normalizados). */
export interface EditRouteDataToCompare {
  name: string;
  /** D2 (2026-07-20): descripción real de la Order gemela (`Order.
   *  description` — la entidad SurveillanceRoute NO la trae, ver fragment
   *  `SurveillanceRouteFullFragment`). Baseline del diff del wizard. */
  description: string;
  concessionId: string;
  /** Multi-carretera (2026-08-15): baseline del diff como CONJUNTO. */
  roadIds: string[];
  roadRanges: Array<{ roadId: string; pkInit: string; pkEnd: string }>;
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
  teamId: string;
  /** Ids de operarios ordenados (baseline == deriveCrew(semilla).operatorIds). */
  userIDs: string[];
  /** Parejas vehículo→conductor ordenadas por vehicleId. */
  vehicleAssignments: Array<{ vehicleId: string; driverId: string }>;
}

/** Ordena las parejas por vehicleId para que el diff sea estable. */
const normalizeAssignments = (
  assignments: Array<{ vehicleId: string; driverId: string }>,
): Array<{ vehicleId: string; driverId: string }> =>
  [...assignments].sort((a, b) => a.vehicleId.localeCompare(b.vehicleId));

/** Query tolerante a fallos (un enriquecimiento secundario nunca tumba la página). */
const q = async <T,>(
  query: DocumentNode,
  variables: Record<string, unknown>,
  fetchPolicy: 'cache-first' | 'cache-only' = 'cache-first'
): Promise<T | null> => {
  try {
    const res = await client.query<T>({
      query,
      variables,
      fetchPolicy,
    });
    return res.data ?? null;
  } catch {
    return null;
  }
};

const emptyLoaderData = (routeId: string) => ({
  concessions: [],
  roads: [],
  internalOperators: { operators: [], vehicles: [], teams: [] },
  externalCompanies: [],
  hoursTypeOptions: hoursType,
  teams: [],
  presets: [],
  routeId,
  route: null,
  editInitialValues: null,
  editOperatorsSeed: { operatorsTable: [], vehicleTable: {} },
  dataToCompare: null,
  hasSegments: false,
});

export const LoaderEditRoute: (
  userData: AuthContextType | null,
) => LoaderFunction =
  (userData) =>
  async ({ params }) => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const routeId = params.routeId ?? '';

  /**
   * El cuerpo entero, parametrizado por DE DÓNDE salen los datos.
   *
   * 🔴 Tiene que ser el cuerpo entero y no una consulta suelta. El primer
   * intento protegió solo la del recorrido, que es UN elemento de un
   * `Promise.all`: sus hermanas seguían `network-only` (las cuatro de
   * `selectActiveOperatorsAndVehicles`) o `cache-first` (que sale a la red en
   * cuanto falta el dato), así que con cobertura degradada el asistente seguía
   * colgado exactamente igual. Un `Promise.all` va al ritmo de su elemento más
   * lento, y una petición colgada no lanza: el `try/catch` no la ve.
   */
  const build = async (mode: 'network' | 'cache') => {
      const policy = mode === 'cache' ? 'cache-only' : 'cache-first';
      // 1. Entidad SR (network-only: siempre estado fresco) + opciones del
      //    wizard (mismas fuentes que NewRoute/loader).
      //
      //    Query LIGERA (perf E3, 2026-07-20): `GET_SURVEILLANCE_ROUTE_FOR_EDIT`
      //    en vez de la del detalle — ver `SURVEILLANCE_ROUTE_EDIT_FRAGMENT`
      //    para el detalle de qué trae/por qué. Efecto en caché normalizada:
      //    esta query escribe `SurveillanceRoute:<id>` con SOLO estos campos
      //    (Apollo solo toca los campos presentes en la selección — nunca
      //    borra `gpsPoints`/segments-completos si ya estaban cacheados de
      //    una visita previa al detalle, y tampoco los "rellena" si no lo
      //    estaban). El camino de vuelta al detalle
      //    (`RouteDetailed/loader.ts`) usa `fetchPolicy: 'network-only'` para
      //    ESTA MISMA entidad → SIEMPRE golpea red y reescribe la entidad
      //    completa (incluido `gpsPoints`, que tiene `merge: replace` en
      //    `policies.ts`), así que "detalle ← editar" nunca sirve datos
      //    parciales por date-of-record — no hace falta invalidar nada aquí.
      //    Los consumidores que SÍ leen esta entidad con `cache-first`
      //    (`Breadcrumbs`, `ChildOrdersPage`/`MultimediaPage` loaders) piden
      //    la fragment Full; si el cache solo tiene la parcial de esta query,
      //    Apollo lo trata como cache MISS (selección incompleta) y cae a
      //    red igual que con caché fría — mismo comportamiento ya documentado
      //    en esos ficheros para el caso "deep-link sin visitar el detalle
      //    antes", cero regresión nueva.
      const [
        routeResponse,
        concessionsResponse,
        roadsResponse,
        surveillanceTeamsResponse,
        internalOperators,
        presets,
      ] = await Promise.all([
        mode === 'cache'
          ? Promise.resolve({
              data: client.readQuery({
                query: GET_SURVEILLANCE_ROUTE_FOR_EDIT,
                variables: { id: routeId },
              }) as { surveillanceRoute?: SurveillanceRoute } | null,
            })
          : client.query({
              query: GET_SURVEILLANCE_ROUTE_FOR_EDIT,
              variables: { id: routeId },
              fetchPolicy: 'network-only',
            }),
        client.query({ query: GET_CONCESSIONS, fetchPolicy: policy }),
        client.query({ query: GET_ROADS, fetchPolicy: policy }),
        client.query({
          query: FETCH_SURVEILLANCE_TEAMS,
          fetchPolicy: policy,
        }),
        // Catálogo COMPLETO (activos + inactivos), mismo patrón que
        // ActionsValidation/OperatorsAndVehicles/loader.ts:58 (editar/validar
        // Actuaciones). Editar un RV con un operario/vehículo ya asignado que
        // pasó a inactivo necesita verlo en el catálogo para conservarlo —
        // `true` (solo activos) lo hacía desaparecer del seed y del diff.
        // NewRoute (crear) sigue en `true`, sin tocar.
        selectActiveOperatorsAndVehicles(false, {
          fetchPolicy: mode === 'cache' ? 'cache-only' : 'network-only',
        }),
        // Plantillas de recorrido (2026-08-16): el selector de nombre también
        // aparece al EDITAR, así que lee el catálogo por el mismo sitio que el
        // asistente de creación. Sin plantillas o sin red, la pantalla se
        // comporta como antes.
        readSurveillanceRoutePresets(),
      ]);

      const route: SurveillanceRoute | null =
        routeResponse.data?.surveillanceRoute ?? null;

      if (!route) {
        return emptyLoaderData(routeId);
      }

      const concessions = concessionsResponse.data?.concessions || [];
      const roads = roadsResponse.data?.roads || [];
      const surveillanceTeams = (
        surveillanceTeamsResponse.data?.surveillanceTeams || []
      ).map((tm: { id: string; name: string }) => ({ id: tm.id, name: tm.name }));
      const catalog = (internalOperators ?? {
        operators: [],
        vehicles: [],
        teams: [],
      }) as {
        operators: OperatorsSelectionType[];
        vehicles: VehiclesSelectionType[];
        teams: unknown[];
      };
      // `selectActiveOperatorsAndVehicles(false)` (punto 1, arriba) trae el
      // catálogo COMPLETO de vehículos SIN el filtro de uso que sí aplica el
      // path `true` (ver src/gql/selectors/actions.ts:181-184) — necesario
      // para conservar vehículos CONSTRUCTION inactivos ya asignados, pero
      // como efecto colateral también arrastraba vehículos de uso ACCIDENT.
      // Un RV (patrulla/obra) solo puede usar vehículos CONSTRUCTION, así que
      // reaplicamos aquí el mismo criterio (`usage === CONSTRUCTION`) sobre
      // el catálogo ya completo: mantiene los CONSTRUCTION inactivos y
      // descarta los ACCIDENT, sin volver a esconder el vehículo inactivo
      // que C1 quería conservar.
      catalog.vehicles = (catalog.vehicles || []).filter(
        (v) =>
          (v as unknown as { usage?: string }).usage === VehicleUsage.CONSTRUCTION,
      );

      // 2. Una fila por carretera del recorrido, con los límites de SU vía
      //    (necesarios para que `pkValidation` del schema pase con los valores
      //    precargados) y el rango que el recorrido declara en ella.
      //
      //    Multi-carretera (2026-08-15): antes era una sola carretera con un
      //    par de PK. Los PK del recorrido son los de la PRIMERA; las demás
      //    salen de `roadRanges`, y si no lo tienen caen a los extremos de su
      //    propia vía — que es lo que el backend usa para validar.
      const currentRoadIds = route.roadIDs ?? [];
      const declaredRanges = route.roadRanges ?? [];

      const roadRangeRows = await Promise.all(
        currentRoadIds.map(async (roadId, index) => {
          // Fallback: los PK del propio RV, que es lo que había antes de que
          // existieran los rangos por carretera.
          let pkInitRoad = pkFormatter(route.pkInitKm, route.pkInitMeter);
          let pkEndRoad = pkFormatter(route.pkEndKm, route.pkEndMeter);
          const roadData = await q<{
            road: {
              pkInitKm: number;
              pkInitMeter: number;
              pkEndKm: number;
              pkEndMeter: number;
            } | null;
          }>(GET_ROAD_BY_ID, { id: roadId }, policy);
          if (roadData?.road) {
            pkInitRoad = pkFormatter(
              roadData.road.pkInitKm,
              roadData.road.pkInitMeter,
            );
            pkEndRoad = pkFormatter(
              roadData.road.pkEndKm,
              roadData.road.pkEndMeter,
            );
          }

          const declared = declaredRanges.find((r) => r.roadId === roadId);
          if (declared) {
            return {
              roadId,
              pkInitRoad,
              pkEndRoad,
              pkInit: pkFormatter(declared.pkInitKm, declared.pkInitMeter),
              pkEnd: pkFormatter(declared.pkEndKm, declared.pkEndMeter),
            };
          }
          // Sin rango propio: la PRIMERA carretera usa los PK del recorrido
          // (que son suyos por definición) y las demás, los extremos de su vía.
          return index === 0
            ? {
                roadId,
                pkInitRoad,
                pkEndRoad,
                pkInit: pkFormatter(route.pkInitKm, route.pkInitMeter),
                pkEnd: pkFormatter(route.pkEndKm, route.pkEndMeter),
              }
            : { roadId, pkInitRoad, pkEndRoad, pkInit: pkInitRoad, pkEnd: pkEndRoad };
        }),
      );

      // 2b. Descripción de la Order gemela (D2 2026-07-20). La entidad
      //     SurveillanceRoute NO trae `description` (comentario en el fragment:
      //     "→Order.description"), así que se resuelve por separado con el
      //     MISMO patrón que `RouteDetailed/loader.ts` (`GET_ORDERS_BY_IDS_SERVER`,
      //     `ids: [orderId]`, cache-first) — tolerante a fallos vía `q`, un
      //     enriquecimiento secundario nunca debe tumbar el wizard.
      const twinOrderId = route.orderIDs?.[0] ?? '';
      let description = '';
      if (twinOrderId) {
        const orderData = await q<{
          ordersByIds: Array<{ id: string; description?: string | null }>;
        }>(GET_ORDERS_BY_IDS_SERVER, { ids: [twinOrderId] }, policy);
        description = orderData?.ordersByIds?.[0]?.description ?? '';
      }

      // 3-4. Tripulación actual — operarios (filtro del catálogo COMPLETO,
      //      activos + inactivos, punto 1) y parejas vehículo→conductor. Un
      //      operario inactivo ya asignado entra aquí, en la baseline
      //      `dataToCompare.userIDs` (abajo) y en el `set` del guardado — se
      //      conserva en vez de desaparecer. La baseline del diff usa ESTOS
      //      mismos ids para no producir un diff espurio contra la semilla.
      //      Lógica compartida con «Mover tramos» (`crewSeed.ts`).
      const { operatorsTable, vehicleTable, assignments } =
        await buildRouteCrewSeed(route, catalog, async (id) => {
          const data = await q<{
            vehicleOccupant: {
              id: string;
              vehicleIDs?: string[];
              userDriverIDs?: string[];
            } | null;
          }>(GET_VEHICLE_OCCUPANT_BY_ID, { id }, policy);
          return data?.vehicleOccupant ?? null;
        });

      // 5. Valores precargados de Ubicació.
      const editInitialValues = {
        name: route.name ?? '',
        description,
        concessionId: route.concessionIDs?.[0] ?? '',
        roadIds: currentRoadIds,
        roadRanges: roadRangeRows,
        teamId: route.teamIDs?.[0] ?? '',
      };

      // 6. Snapshot para el diff (números/ids canónicos).
      const dataToCompare: EditRouteDataToCompare = {
        name: route.name ?? '',
        description,
        concessionId: route.concessionIDs?.[0] ?? '',
        roadIds: currentRoadIds,
        roadRanges: roadRangeRows.map((r) => ({
          roadId: r.roadId,
          pkInit: r.pkInit,
          pkEnd: r.pkEnd,
        })),
        pkInitKm: route.pkInitKm,
        pkInitMeter: route.pkInitMeter,
        pkEndKm: route.pkEndKm,
        pkEndMeter: route.pkEndMeter,
        teamId: route.teamIDs?.[0] ?? '',
        userIDs: [...operatorsTable.map((op) => op.id)].sort(),
        vehicleAssignments: normalizeAssignments(assignments),
      };

      return {
          // Compatible con NewRouteLoaderData (lo lee el wizard NewRoute).
          concessions,
          roads,
          internalOperators: catalog,
          externalCompanies: [],
          hoursTypeOptions: hoursType,
          teams: surveillanceTeams,
          presets,
          // Extras de edición (los lee la página EditRoute).
          routeId,
          route,
          editInitialValues,
          editOperatorsSeed: { operatorsTable, vehicleTable },
          dataToCompare,
          // La fragment de edición pide `segments { id }` (mínimo posible)
          // solo para este conteo — hay tramos si el array no está vacío
          // (§2.5). Ver `SURVEILLANCE_ROUTE_EDIT_FRAGMENT`.
          hasSegments: (route.segments?.length ?? 0) > 0,
        };
  };

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    if (!routeId || !userData?.user) {
      return emptyLoaderData(routeId);
    }

    try {
      return await preferServerWithCacheFallback(build('network'), async () => {
        const cached = await build('cache');
        // 🔴 `null`, no el vacío: sin recorrido en caché no hay nada que
        // adelantar, y devolver `emptyLoaderData` echaría al operario del
        // asistente —la pantalla navega fuera cuando no hay valores iniciales—
        // convirtiendo una espera en una expulsión. Con `null`, el respaldo se
        // abstiene y se sigue esperando al servidor, como antes.
        return cached.editInitialValues ? cached : null;
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error loading edit route data:', error);
      return emptyLoaderData(routeId);
    }
  };

  return { data: load() };
};

export default LoaderEditRoute;
